/**
 * Asking for this model's share card to exist (OG-SPEC §8.1).
 *
 * ## Why the browser has to be the one to ask
 *
 * A social crawler does not run JavaScript, so by the time one arrives the card
 * must already be a PNG in a bucket. Nothing in the browser can render it —
 * that needs the Hub, a rasterizer and minutes — so the only thing a visitor's
 * page can contribute is the *knowledge that this model was looked at*, which
 * is precisely what nothing else has.
 *
 * The alternative was generating on the crawler's request, which M5 built and
 * §20 replaced: it made the card's existence depend on a crawler being patient,
 * and forced the CDN cache to vary on `User-Agent`.
 *
 * ## What this sends, exactly
 *
 * `{repoId, sha}`. Both are already in the address bar; both were typed or
 * clicked by the person looking at them. No inventory, no tensor names, no
 * statistics, no cookie, no identifier — and nothing at all from a page that
 * has not loaded a model.
 *
 * That is the whole of the privacy cost, and it is stated in `llms.txt` rather
 * than described around.
 *
 * ## Why every failure is silent
 *
 * A share card is a nicety. If this call fails — offline, rate-limited,
 * blocked by an extension, the service down — the visitor's page is unaffected
 * and a shared link falls back to the generic card, which is exactly M5's
 * behaviour. So there is no error to show, and showing one would teach people
 * to worry about something that does not concern them.
 *
 * ## Why it is sent unconditionally rather than after a check
 *
 * §8.1. The obvious optimisation is to `HEAD` the card first and skip the call
 * when it exists — but a missing card is served as the generic image with a
 * **200**, so that URL cannot truthfully answer the question. A check would
 * always say "exists" and the card would never be generated.
 */

/*
 * The payload hash moved to `util/body-hash.ts` when `/report` needed the same
 * header for the same reason (REPORT-SPEC §7.2). It is one function shared by
 * both POSTs rather than two that could differ: a copy that hashed the string
 * instead of its UTF-8 bytes would fail signature verification at the edge, on
 * one endpoint only, with nothing naming the difference.
 */
import { bodyHash } from "../util/body-hash.js";

/**
 * Same-origin, deliberately (OG-SPEC §13.2).
 *
 * This was an absolute URL on `og.tensorlens.dev` until that distribution's
 * 403/404-to-PNG rule — which exists so a crawler never caches a missing card
 * as absent — turned every error here into an image served at status 200.
 * Custom error responses have no per-behaviour form, so the endpoint moved
 * rather than the rule.
 *
 * Being same-origin also means no CORS, no preflight, and `connect-src 'self'`
 * instead of a named origin of ours: the CSP got shorter for this feature, not
 * longer.
 */
const PREPARE_ENDPOINT = "/prepare";

/** 40 lowercase hex. A branch name cannot pin a card and is never sent. */
const COMMIT_SHA = /^[0-9a-f]{40}$/;

/**
 * Models already asked for in this tab.
 *
 * Not a cache of the answer — a record of having asked. Compare mode, a metric
 * change and a tensor selection all re-render without changing the model, and
 * asking again on each would multiply the requests for no possible effect on
 * the card. The service is idempotent, so this is politeness rather than
 * correctness.
 */
const asked = new Set<string>();

/**
 * Ask, at most once per model per tab. Never throws, never returns a failure.
 *
 * Called as soon as the repo is known to have safetensors, *before* its headers
 * are read — not after the load finishes. The browser and the generator do the
 * same sequential header work, so on a large model the two runs overlap and the
 * card is usually ready before the visitor has finished looking. See the call
 * site in `main.ts` for why not earlier still.
 *
 * Deliberately not awaited by callers: it is fire-and-forget by design, and a
 * page that waited on it would be waiting on something that cannot change what
 * it shows.
 */
export function prepareShareCard(repoId: string, sha: string, part?: string): void {
  if (!COMMIT_SHA.test(sha)) return;
  /*
   * An empty part is no part, and this normalises rather than trusts the caller.
   *
   * `componentLabel` used to return `""` for a single-model repo, which is what
   * the root component's prefix literally is. The body below tests
   * `part === undefined`, so `""` sailed past it and every ordinary safetensors
   * repo asked for `{repoId, sha, part: ""}` — which `/prepare` rejects with
   * `400 {"status":"invalid"}`. Nothing surfaced: the ask swallows its failures
   * on purpose, so the symptom was share cards silently not existing.
   *
   * That caller is fixed. This line is here because the bug was a falsy value
   * crossing a boundary that only checked for `undefined`, and the boundary is
   * the right place to stop it — the request builder is what knows that an empty
   * part is not a part.
   */
  const wanted = part === "" ? undefined : part;
  // The part is in the key, because it is a different card: a GGUF repo's
  // quantizations span 2.89 to 8.53 bits per weight and each draws its own map.
  // Without it, switching part in one tab would be recorded as "already asked".
  // Keyed off the normalised value, so an empty part and no part are one ask.
  const key = `${repoId}@${sha}@${wanted ?? ""}`;
  if (asked.has(key)) return;
  asked.add(key);

  const body = wanted === undefined ? { repoId, sha } : { repoId, sha, part: wanted };
  const ask = send(JSON.stringify(body)).catch(() => {
    // See the file comment. A share card is a nicety; its absence is invisible
    // to this page and falls back to the generic card.
  });
  inFlight = Promise.all([inFlight, ask]).then(() => undefined);
}

/**
 * Test seam: something to await, for a function whose whole point is that
 * nobody awaits it.
 *
 * The ask became asynchronous when it started hashing its own body, so a test
 * that asserted straight after the call saw nothing. The obvious fix — sleep a
 * tick — is a race dressed as a test: `crypto.subtle.digest` does not promise
 * to resolve before a zero-delay timer, and the version of this file that slept
 * failed intermittently and passed on a rerun.
 *
 * Combined rather than chained, so asks still overlap exactly as they do in a
 * browser; this only observes them.
 */
let inFlight: Promise<void> = Promise.resolve();

export function preparedSettledForTests(): Promise<void> {
  return inFlight;
}

/**
 * A 4xx is our bug; everything else is the nicety failing gracefully.
 *
 * This used to `await fetch(...)` and look at nothing, so a rejection by our own
 * endpoint resolved exactly like a success — the catch upstream never even ran.
 * When `part: ""` started going out, `/prepare` answered `400
 * {"status":"invalid"}` on every safetensors repo and *no layer anywhere said
 * so*: not the browser, not the prepare log (which records no repo id), not the
 * queue, not the DLQ. The only visible symptom was cards quietly not existing,
 * three hours later, found by someone opening a model and looking.
 *
 * So the two cases are separated. A network error, an offline visitor, a
 * `crypto.subtle` that needs a secure context — those are the documented
 * fire-and-forget path and stay silent. A 4xx means the browser built a request
 * its own service refuses, which is a defect that should never reach a visitor
 * and which nobody can fix without being told. It goes to the console and no
 * further: there is no reporter to send it to and adding one would be a
 * `connect-src` argument, not a fix.
 */
async function send(body: string): Promise<void> {
  const response = await fetch(PREPARE_ENDPOINT, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      "x-amz-content-sha256": await bodyHash(body),
    },
    body,
    /*
     * No credentials, ever. There are none to send and asking for them would
     * make this a cross-origin request that *could* carry identity — which is
     * the thing the whole design is arranged to avoid.
     */
    credentials: "omit",
    // `keepalive` so the ask survives a visitor who clicks away immediately,
    // which is the common case for someone checking one model and sharing it.
    keepalive: true,
  });
  if (response.status >= 400 && response.status < 500) {
    console.warn(
      `tensorlens: /prepare rejected this ask with ${response.status}. ` +
        `The share card will not be generated. Body was: ${body}`,
    );
  }
}

/** Test seam: the set is module state and would otherwise leak between cases. */
export function resetPreparedForTests(): void {
  asked.clear();
}
